import React, { useState, useEffect, useRef } from 'react';
import { aiAnalysisService, handleApiError } from '../utils/apiClient';
import { config } from '../config/config';
import { trackEvent } from '../utils/analytics';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PDFPreview from './PDFPreview';
import SkinTips from './SkinTips';

const DocsAnalysis = ({
  nextStep,
  previousStep,
  goToStep,
  updateAnalysisResult,
  appState,
  setLoading,
  setError,
  doctorWhatsAppUrl
}) => {
  // Get doctor information from URL path or query params
  const getDoctorInfo = () => {
    const urlPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    // Check URL params first (for cases like /docs?doctor=adityashah)
    const doctorParam = urlParams.get('doctor') || urlParams.get('clinic') || urlParams.get('ref');

    // Check if we're on a doctor-specific route (like /adityashah, /janakthakkar, etc.)
    const pathSegments = urlPath.split('/').filter(segment => segment);
    const firstSegment = pathSegments[0];

    // Check if first segment is 'docs' - if so, use general my-skin.ai branding unless doctor param exists
    if (firstSegment === 'docs' && !doctorParam) {
      return {
        name: 'my-skin.ai',
        clinic: 'my-skin.ai',
        logo: '/assets/LOGO.svg'
      };
    }

    // Doctor information mapping - updated to match actual file names
    const doctorMapping = {
      'adityashah': {
        name: 'Dr. Aditya Shah',
        clinic: 'Aura Laser & Cosmetic Clinic',
        logo: '/assets/adityashah.svg'
      },
      'Janakthakkar': {
        name: 'Dr. Janak Thakkar',
        clinic: 'Orna Skin',
        logo: '/assets/janakthakkar.svg'
      },
      'apratimgoel': {
        name: 'Dr. Apratim Goel',
        clinic: 'Oladerm',
        logo: '/assets/oladerm.svg'
      },
      'Apratimgoel': {
        name: 'Dr. Apratim Goel',
        clinic: 'Oladerm',
        logo: '/assets/oladerm.svg'
      },
      'apratim-goel': {
        name: 'Dr. Apratim Goel',
        clinic: 'Oladerm',
        logo: '/assets/oladerm.svg'
      },
      'oladerm': {
        name: 'Dr. Apratim Goel',
        clinic: 'Oladerm',
        logo: '/assets/oladerm.svg'
      },
      'Oladerm': {
        name: 'Dr. Apratim Goel',
        clinic: 'Oladerm',
        logo: '/assets/oladerm.svg'
      },
      'smitthakkar': {
        name: 'Dr. Smit Thakkar',
        clinic: 'OrnaSkin Laser & Hair care clinic',
        logo: '/assets/janakthakkar.svg'
      },
      'Smitthakkar': {
        name: 'Dr. Smit Thakkar',
        clinic: 'OrnaSkin Laser & Hair care clinic',
        logo: '/assets/janakthakkar.svg'
      },
      'smitthakker': {
        name: 'Dr. Smit Thakkar',
        clinic: 'OrnaSkin Laser & Hair care clinic',
        logo: '/assets/janakthakkar.svg'
      },
      'Smitthakker': {
        name: 'Dr. Smit Thakkar',
        clinic: 'OrnaSkin Laser & Hair care clinic',
        logo: '/assets/janakthakkar.svg'
      },
      'harshitranpata': {
        name: 'Dr. Harshit Ranpata',
        clinic: 'ADIVA Center for Skin and Hair',
        logo: '/assets/harshitranpara.svg'
      },
      'harshitranpara': {
        name: 'Dr. Harshit Ranpara',
        clinic: 'ADIVA Center for Skin and Hair',
        logo: '/assets/harshitranpara.svg'
      },
      'vishalchugh': {
        name: 'Dr. Vishal Chugh',
        clinic: 'Radiant Skin Clinic',
        logo: '/assets/vishalchugh.svg'
      },
      'piyushborkhatariya': {
        name: 'Dr. Piyush Borkhatariya',
        clinic: 'ESTHE Kayakalp Skin Hair Laser & Hair Transplant Clinic',
        logo: '/assets/piyushborkhatria.svg'
      },
      'piyushborkhatria': {
        name: 'Dr. Piyush Borkhatria',
        clinic: 'ESTHE Kayakalp Skin Hair Laser & Hair Transplant Clinic',
        logo: '/assets/piyushborkhatria.svg'
      },
      'geetanjalisheety': {
        name: 'Dr. Geetanjali Sheety',
        clinic: 'revitalis',
        logo: '/assets/geetanjalishetty.svg'
      },
      'geetanjalishetty': {
        name: 'Dr. Geetanjali Shetty',
        clinic: 'revitalis',
        logo: '/assets/geetanjalishetty.svg'
      },
      'bschandrashekhar': {
        name: 'Dr. B S Chandrashekhar',
        clinic: 'Cutis Epicorium Skin Clinic HSR',
        logo: '/assets/bschandrashekhar.svg'
      },
      'prashantagrawal': {
        name: 'Dr. Prashant Agrawal',
        clinic: 'Dermadent Clinic',
        logo: '/assets/Prashantagrawal.svg'
      },
      'Prashantagrawal': {
        name: 'Dr. Prashant Agrawal',
        clinic: 'Dermadent Clinic',
        logo: '/assets/Prashantagrawal.svg'
      },
      'amandua': {
        name: 'Dr. Aman Dua',
        clinic: 'AK Clinic',
        logo: '/assets/amandua.svg'
      },
      'rohanbhattacharjee': {
        name: 'Dr. Rohan Bhattacharjee',
        clinic: "Dr. Rohan's Skin, LASERs, & Hair transplantation clinic",
        logo: '/assets/rohanbhattacharjee.svg'
      }
    };

    // Check doctor param first, then path segment
    const doctorSlug = doctorParam || firstSegment;

    if (doctorSlug && doctorMapping[doctorSlug]) {
      return {
        ...doctorMapping[doctorSlug],
        slug: doctorSlug,  // Include slug for tracking
        isDoctor: true     // Flag to identify doctor pages
      };
    }

    // Default fallback to my-skin.ai (not a doctor page)
    return {
      name: 'my-skin.ai',
      clinic: 'my-skin.ai',
      logo: '/assets/LOGO.svg',
      slug: null,
      isDoctor: false
    };
  };

  const doctorInfo = getDoctorInfo();

  const [analysisState, setAnalysisState] = useState({
    isAnalyzing: false,
    progress: 0,
    currentStep: '',
    result: appState.analysisResult || null,
    error: null,
    retryCount: 0,
    phase: 'preparing',
    technicalUpdate: '',
    skinTip: ''
  });

  const [showDetailedView, setShowDetailedView] = useState(false);
  const [pdfState, setPdfState] = useState({
    isGenerating: false,
    isGenerated: false,
    pdfBlob: null,
    error: null
  });
  const pdfPreviewRef = useRef(null);
  const analysisInitiatedRef = useRef(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsPayload, setDetailsPayload] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  // Start analysis when component mounts (if not already analyzed)
  useEffect(() => {
    // Add a small delay to ensure all data is properly loaded
    const timeoutId = setTimeout(() => {
      if (!analysisState.result && appState.capturedImage && appState.patientData && !analysisState.isAnalyzing && !analysisInitiatedRef.current) {
        // Starting AI analysis
        analysisInitiatedRef.current = true;
        startAnalysis();
      }
    }, 500); // 500ms delay to ensure component is fully mounted

    return () => clearTimeout(timeoutId);
  }, [analysisState.result, appState.capturedImage, appState.patientData]);

  // Cleanup download URL on unmount
  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  // Real-time progress tracking
  const updateProgress = (step, progress) => {
    setAnalysisState(prev => ({
      ...prev,
      currentStep: step,
      progress: progress
    }));
  };

  // Start AI analysis with real-time progress
  const startAnalysis = async () => {
    // Prevent multiple simultaneous analysis calls
    if (analysisState.isAnalyzing) {
      return;
    }

    if (!appState.capturedImage || !appState.patientData) {
      setError('Missing required data for analysis. Please go back and complete previous steps.');
      return;
    }

    setAnalysisState(prev => ({
      ...prev,
      isAnalyzing: true,
      error: null,
      progress: 0
    }));

    try {
      // Real-time progress updates
      updateProgress('Initializing AI analysis...', 10);

      // Prepare patient context for better analysis
      const patientContext = {
        name: appState.patientData.name,
        age: appState.patientData.age,
        gender: appState.patientData.gender,
        email: appState.patientData.email,
        phone: appState.patientData.phone,
        city: appState.patientData.city || null,
        state: appState.patientData.state || null
      };

      // DEBUG: Log what's in appState.patientData

      updateProgress('Uploading image securely...', 25);

      // Add a small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));
      updateProgress('Connecting to AI service...', 40);

      // Call AI Analysis API with progress tracking
      const analysisPromise = aiAnalysisService.analyzeImage(
        appState.capturedImage.base64,
        patientContext
      );

      // Enhanced 20-second progress with realistic phases
      const technicalUpdates = [
        'Analyzing 468 facial landmarks...',
        'Processing skin texture data...',
        'Detecting pore visibility patterns...',
        'Evaluating pigmentation consistency...',
        'Measuring wrinkle depth analysis...',
        'Comparing with dermatological database...',
        'Processing color histogram data...',
        'Analyzing skin elasticity indicators...'
      ];

      const skinTips = [
        'Did you know? Drinking 8 glasses of water daily helps maintain skin elasticity',
        'Tip: SPF 30+ protects against 97% of harmful UV rays',
        'Fact: Vitamin C serum can boost collagen production by up to 30%',
        'Pro tip: Gentle cleansing twice daily prevents premature aging',
        'Research shows: 7-9 hours of sleep improves skin cell regeneration',
        'Expert advice: Retinol is most effective when applied at night',
        'Study fact: Antioxidants help repair daily environmental damage'
      ];

      let currentTechIndex = 0;
      let currentTipIndex = 0;
      let progressStartTime = Date.now();
      let hasApiReturned = false;

      const progressTimer = setInterval(() => {
        setAnalysisState(prev => {
          const elapsedTime = Date.now() - progressStartTime;
          const targetProgress = Math.min(100, (elapsedTime / 25000) * 100); // 25 second duration

          // Update progress based on elapsed time
          let newProgress = targetProgress;
          let newPhase = prev.phase;
          let currentStep = prev.currentStep;

          // Multi-stage progression
          if (newProgress < 25) {
            newPhase = 'preparing';
            currentStep = 'Preparing image for analysis...';
          } else if (newProgress < 50) {
            newPhase = 'detecting';
            currentStep = 'Detecting facial landmarks...';
          } else if (newProgress < 75) {
            newPhase = 'analyzing';
            currentStep = 'Analyzing skin texture and tone...';
          } else if (newProgress < 90) {
            newPhase = 'processing';
            currentStep = 'Processing skin health indicators...';
          } else {
            newPhase = 'finalizing';
            currentStep = 'Generating personalized insights...';
          }

          return {
            ...prev,
            progress: newProgress,
            currentStep,
            phase: newPhase
          };
        });
      }, 250); // Update every 250ms for smooth animation

      // Rotating technical updates every 3 seconds
      const techUpdateTimer = setInterval(() => {
        setAnalysisState(prev => ({
          ...prev,
          technicalUpdate: technicalUpdates[currentTechIndex % technicalUpdates.length]
        }));
        currentTechIndex++;
      }, 3000);

      // Rotating skin tips every 4 seconds
      const tipUpdateTimer = setInterval(() => {
        setAnalysisState(prev => ({
          ...prev,
          skinTip: skinTips[currentTipIndex % skinTips.length]
        }));
        currentTipIndex++;
      }, 4000);

      // Set initial updates
      setTimeout(() => {
        setAnalysisState(prev => ({
          ...prev,
          technicalUpdate: technicalUpdates[0],
          skinTip: skinTips[0]
        }));
      }, 1000);

      const response = await analysisPromise;
      hasApiReturned = true;

      clearInterval(progressTimer);
      clearInterval(techUpdateTimer);
      clearInterval(tipUpdateTimer);

      updateProgress('Analysis complete!', 100);

      if (response.success) {
        // Schema validation and normalization handled server-side for security
        const normalizedData = response.data;

        const analysisResult = {
          ...normalizedData,
          timestamp: new Date().toISOString(),
          patientId: `${appState.patientData.name}_${Date.now()}`,
          imageInfo: {
            source: appState.capturedImage.source,
            timestamp: appState.capturedImage.timestamp
          },
          confidence: 'AI Assessment',
          processingTime: Date.now() - new Date(appState.capturedImage.timestamp).getTime()
        };

        updateProgress('Analysis complete!', 100);

        setAnalysisState(prev => ({
          ...prev,
          isAnalyzing: false,
          result: analysisResult
        }));

        updateAnalysisResult(analysisResult);
        // Track with doctor_id (slug) to distinguish doctor pages from main site
        trackEvent('report_generated', {
          doctor_id: doctorInfo.slug,  // Will be null for main site, slug for doctor pages
          meta: {
            source: doctorInfo.isDoctor ? 'doctor_page' : 'main_site',
            clinic_name: doctorInfo.clinic
          }
        });

      } else {
        // Show real API errors - no fallback
        throw new Error(response.error || 'AI analysis failed - no response received');
      }

    } catch (error) {
      // Clear all running timers
      if (typeof progressTimer !== 'undefined') {
        clearInterval(progressTimer);
      }
      if (typeof techUpdateTimer !== 'undefined') {
        clearInterval(techUpdateTimer);
      }
      if (typeof tipUpdateTimer !== 'undefined') {
        clearInterval(tipUpdateTimer);
      }

      // Handle real errors transparently
      let errorMessage = 'Unable to analyze this image. Please try again with a different photo.';

      if (error.message.includes('content guidelines') || error.message.includes('policy')) {
        errorMessage = 'Unable to analyze this image. Please try again with a clear photo of your face with good lighting.';
      } else if (error.message.includes('API key') || error.message.includes('401')) {
        errorMessage = 'Analysis service temporarily unavailable. Please try again in a few minutes.';
      } else if (error.message.includes('Rate limit') || error.message.includes('429')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Analysis is taking too long. Please try again with a smaller image or better lighting.';
      } else if (error.message.includes('compression') || error.message.includes('size')) {
        errorMessage = 'Image is too large or complex. Please try again with a smaller, clearer image.';
      } else {
        errorMessage = 'Unable to analyze this image. Please try again with a different photo with better lighting.';
      }

      setAnalysisState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: errorMessage,
        retryCount: prev.retryCount + 1,
        progress: 0
      }));

      setError(errorMessage);
    }
  };

  // Retry analysis
  const retryAnalysis = () => {
    if (analysisState.retryCount < 3) {
      // Retrying analysis
      // Reset the initiated flag for retry
      analysisInitiatedRef.current = false;
      setAnalysisState(prev => ({
        ...prev,
        error: null,
        retryCount: prev.retryCount + 1
      }));
      setError(null);
      // Small delay before retry to ensure clean state
      setTimeout(() => {
        analysisInitiatedRef.current = true;
        startAnalysis();
      }, 100);
    } else {
      setError('Unable to analyze this image after multiple attempts. Please try again with a different, clearer photo.');
    }
  };

  // Get severity styling
  const getSeverityStyle = (severity) => {
    const severityLower = severity?.toLowerCase() || '';

    if (severityLower.includes('mild')) {
      return 'severity-mild';
    } else if (severityLower.includes('moderate')) {
      return 'severity-moderate';
    } else if (severityLower.includes('severe')) {
      return 'severity-severe';
    }
    return 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  // Generate PDF directly here
  const generatePDF = async () => {
    if (!analysisState.result || !appState.patientData) {
      setError('Missing data for PDF generation. Please complete analysis first.');
      return;
    }

    setPdfState(prev => ({ ...prev, isGenerating: true, error: null }));

    try {
      // Wait for PDF preview to render and fonts to load
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Additional wait for images to load
      const waitForImages = () => {
        return new Promise((resolve) => {
          const images = pdfPreviewRef.current?.querySelectorAll('img');
          if (!images || images.length === 0) {
            resolve();
            return;
          }

          let loadedCount = 0;
          const totalImages = images.length;

          const checkComplete = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
              resolve();
            }
          };

          images.forEach((img, index) => {
            if (img.complete) {
              checkComplete();
            } else {
              img.onload = () => {
                checkComplete();
              };
              img.onerror = () => {
                checkComplete(); // Continue even if image fails
              };
            }
          });

          // Fallback timeout
          setTimeout(() => {
            resolve();
          }, 2000);
        });
      };

      await waitForImages();

      if (!pdfPreviewRef.current) {
        throw new Error('PDF preview not found');
      }

      // Ensure the component is visible for html2canvas
      const previewElement = pdfPreviewRef.current;
      // Preparing PDF preview dimensions

      if (previewElement.offsetWidth === 0 || previewElement.offsetHeight === 0) {
        // Adjusting PDF preview element dimensions
        previewElement.style.position = 'absolute';
        previewElement.style.left = '0';
        previewElement.style.top = '0';
        previewElement.style.width = '816px';
        previewElement.style.height = 'auto';
        previewElement.style.zIndex = '-1000';
        previewElement.style.opacity = '1';

        // Wait a bit more for the adjustment
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Wait a bit more for logo to load
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Capture the entire PDF preview component with high quality
      const canvas = await html2canvas(pdfPreviewRef.current, {
        scale: 2, // Moderate scale for better compatibility
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f3f4f6', // Match new template background
        quality: 1.0, // Maximum quality
        logging: false, // Disable logging for cleaner output
        width: 794, // A4 width
        height: 2246, // Match total height (2 pages * 1123px each)
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1200, // Window width for better rendering
        windowHeight: 2500, // Adjusted window height
        removeContainer: false,
        imageTimeout: 60000, // Increased timeout for SVGs
        foreignObjectRendering: true,
        letterRendering: true,
        ignoreElements: false,
        onclone: (clonedDoc) => {
          // Ensure all fonts are loaded in cloned document
          const fontStyleSheet = document.createElement('style');
          fontStyleSheet.innerHTML = `
            @import url('https://fonts.googleapis.com/css2?family=Satoshi:wght@400;500;700;900&display=swap');
            * {
              font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif !important;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              text-rendering: geometricPrecision;
            }
            svg {
              shape-rendering: geometricPrecision;
              text-rendering: geometricPrecision;
            }
          `;
          clonedDoc.head.appendChild(fontStyleSheet);

          // Wait for fonts to load
          return new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
        }
      });

      // Create PDF with optimal settings
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 16,
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Convert to PNG for maximum quality
      const imgData = canvas.toDataURL('image/png', 1.0);

      // If a PDF preview component is present, slice by page containers
      const host = document.querySelector('[data-pdf-root]') || document.body;
      const page1El = host.querySelector('#pdf-page-1');
      const page2El = host.querySelector('#pdf-page-2');
      const rectHost = host.getBoundingClientRect();
      const scaleFactor = canvas.height / rectHost.height;

      const mkSlice = (el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return {
          sy: Math.max(0, Math.round((r.top - rectHost.top) * scaleFactor)),
          sh: Math.max(1, Math.round(r.height * scaleFactor))
        };
      };

      const slices = [mkSlice(page1El), mkSlice(page2El)].filter(Boolean);
      if (slices.length === 0) {
        // Fallback: center full image and split after first page height
        const mmHeight = imgHeight;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, Math.min(pageHeight, mmHeight), undefined, 'SLOW');
        if (mmHeight > pageHeight + 0.01) {
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, -pageHeight, imgWidth, mmHeight, undefined, 'SLOW');
        }
      } else {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        const ctx = pageCanvas.getContext('2d');
        slices.forEach((slice, idx) => {
          if (slice.sh < 5) return;
          pageCanvas.height = slice.sh;
          ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, slice.sy, canvas.width, slice.sh, 0, 0, pageCanvas.width, pageCanvas.height);
          const data = pageCanvas.toDataURL('image/png', 1.0);
          if (idx > 0) pdf.addPage();
          const mmHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
          pdf.addImage(data, 'PNG', 0, 0, imgWidth, Math.min(pageHeight, mmHeight), undefined, 'SLOW');
        });
      }

      const pdfBlob = pdf.output('blob');

      // Create download URL
      const url = URL.createObjectURL(pdfBlob);
      setDownloadUrl(url);

      // Store PDF and mark as generated
      updateAnalysisResult({
        ...analysisState.result,
        pdfBlob: pdfBlob,
        pdfGenerated: true
      });

      // Show success animation for docs version (no redirect, just show download)
      setTimeout(() => {
        setPdfState(prev => ({ ...prev, isGenerating: false, isGenerated: true }));
      }, 1500);

    } catch (error) {
      // PDF generation failed
      setPdfState(prev => ({
        ...prev,
        isGenerating: false,
        error: 'PDF generation failed. Please try again.'
      }));
      setError('Failed to generate PDF. Please try again.');
    }
  };

  // Loading state - Split screen with ads
  if (analysisState.isAnalyzing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">

        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Left Side - Analysis Progress */}
          <div className="flex items-center justify-center p-6">
            <div className="w-full max-w-md">
              <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 text-center animate-fade-in-up border border-gray-100">
                {/* Doctor Logo */}
                <div className="mb-6">
                  <div className="w-40 h-28 mx-auto mb-4 flex items-center justify-center">
                    <img
                      src={doctorInfo.logo}
                      alt={doctorInfo.clinic}
                      className="w-full h-full object-contain opacity-90"
                      onError={(e) => {
                        e.target.src = "/assets/LOGO.svg";
                      }}
                    />
                  </div>

                  <h1 className="text-xl font-bold text-gray-900 mb-2">
                    {doctorInfo.clinic} AI Analysis
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {analysisState.technicalUpdate || 'Initializing analysis...'}
                  </p>
                </div>

                {/* Real-time Progress Circle */}
                <div className="mb-6">
                  <div className="relative w-28 h-28 mx-auto mb-4">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        stroke="#E5E7EB"
                        strokeWidth="6"
                        fill="none"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        stroke="url(#gradient)"
                        strokeWidth="6"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 50}`}
                        strokeDashoffset={`${2 * Math.PI * 50 * (1 - analysisState.progress / 100)}`}
                        className="transition-all duration-300 ease-out"
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#3B82F6" />
                          <stop offset="50%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#EC4899" />
                        </linearGradient>
                      </defs>
                    </svg>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {Math.round(analysisState.progress)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          Complete
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Real-time Status */}
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-blue-800 font-medium text-sm">Processing Image</span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Analysis typically takes 15-30 seconds
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Skin Care Tips */}
          <div className="bg-gray-50 p-6 flex flex-col space-y-4">
            <SkinTips />

            {/* Additional Info */}
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-blue-600 text-sm font-medium">✨ While You Wait</div>
              <div className="text-blue-700 text-xs mt-2 space-y-1">
                <p>Our AI analyzes over 50 skin conditions</p>
                <p>Results are for informational purposes only</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout - Stack vertically */}
        <style jsx>{`
          @media (max-width: 1024px) {
            .grid {
              display: block !important;
            }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (analysisState.error && !analysisState.result) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card-lg text-center animate-fade-in-up">
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Analysis Failed
            </h2>
            <p className="text-gray-600 mb-6">
              We encountered an error while analyzing your image
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <p className="text-red-800 mb-4">{analysisState.error}</p>

            {analysisState.retryCount < 3 ? (
              <button onClick={retryAnalysis} className="btn-primary">
                Try Again ({3 - analysisState.retryCount} attempts remaining)
              </button>
            ) : (
              <div className="space-y-4">
                <p className="text-red-700 font-medium">Maximum retry attempts reached</p>
                <div className="flex justify-center space-x-4">
                  <button onClick={previousStep} className="btn-secondary">
                    Go Back
                  </button>
                  <a
                    href={`mailto:${config.app.supportEmail}`}
                    className="btn-primary"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Tap-to-view details modal for each condition
  const openDetails = (title, data) => {
    setDetailsPayload({
      title,
      percentage: data?.percentage,
      description: data?.description,
      severity: data?.severity || (data?.percentage <= 30 ? 'Low' : data?.percentage <= 70 ? 'Moderate' : 'High')
    });
    setDetailsOpen(true);
  };
  const closeDetails = () => setDetailsOpen(false);

  // Helper function to get concern card styling
  const getConcernCardStyle = (urgency) => {
    switch (urgency) {
      case 'high':
        return {
          bgColor: 'bg-red-300',
          textColor: 'text-red-900',
          iconBg: 'bg-red-200',
          level: 'High Priority'
        };
      case 'medium':
        return {
          bgColor: 'bg-amber-300',
          textColor: 'text-amber-900',
          iconBg: 'bg-amber-200',
          level: 'Medium Priority'
        };
      default:
        return {
          bgColor: 'bg-blue-300',
          textColor: 'text-blue-900',
          iconBg: 'bg-blue-200',
          level: 'Good Health'
        };
    }
  };

  // Results state - Modern Dashboard Style (no doctor finder, no priority features)
  if (analysisState.result) {
    const result = analysisState.result;
    const userName = appState.patientData?.name || 'there';
    const currentHour = new Date().getHours();
    const greeting = currentHour < 12 ? 'Good Morning' : currentHour < 17 ? 'Good Afternoon' : 'Good Evening';

    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-50 via-skin-50 to-accent-pink/5">

        {/* Simple Header */}
        <div className="bg-white/60 backdrop-blur-lg border-b border-white/20 px-6 py-4">
          <div className="text-center max-w-md mx-auto">
            <h1 className="text-xl font-bold text-gray-900">
              {greeting}
            </h1>
            <h2 className="text-lg text-gray-700">
              {userName}!
            </h2>
          </div>
        </div>

        {/* Scrollable Analysis Content - Mobile Optimized */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-4 max-w-sm mx-auto">

            {/* User Photo Card - Mobile Optimized */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 animate-fade-in-up">
              <div className="text-center">
                <div className="relative inline-block mb-3">
                  <div className="w-36 h-48 mx-auto rounded-xl overflow-hidden border-3 border-gray-200 shadow-lg">
                    {appState.capturedImage ? (
                      <img
                        src={appState.capturedImage.dataUrl}
                        alt="Your skin analysis"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Status indicator */}
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-md">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Analysis Complete</h3>
                <p className="text-xs text-gray-500">
                  {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Analysis Results Header */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Detailed Analysis</h3>
              <p className="text-sm text-gray-500">
                Comprehensive skin condition breakdown
              </p>
            </div>

            {/* Analysis Results as Minimalistic Cards */}
            <div className="space-y-3">
              {result.detailedAnalysis && Object.entries(result.detailedAnalysis).map(([condition, data], index) => {
                const colors = [
                  { bg: 'bg-pink-50', icon: 'bg-pink-100', text: 'text-pink-600' },
                  { bg: 'bg-blue-50', icon: 'bg-blue-100', text: 'text-blue-600' },
                  { bg: 'bg-green-50', icon: 'bg-green-100', text: 'text-green-600' },
                  { bg: 'bg-purple-50', icon: 'bg-purple-100', text: 'text-purple-600' },
                  { bg: 'bg-orange-50', icon: 'bg-orange-100', text: 'text-orange-600' }
                ];
                const colorSet = colors[index % colors.length];

                return (
                  <div
                    key={condition}
                    className={`${colorSet.bg} rounded-2xl p-4 flex items-center justify-between cursor-pointer active:scale-[0.98] transition-transform`}
                    onClick={() => openDetails(
                      condition.replace(/([A-Z])/g, ' $1').trim(),
                      data
                    )}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 ${colorSet.icon} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <svg className={`w-5 h-5 ${colorSet.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">
                          {condition.replace(/([A-Z])/g, ' $1').trim().replace(' Texture', ' texture')}
                        </div>
                        <div className="text-xs text-gray-500">Detected</div>
                      </div>
                    </div>
                    <div className="text-right flex items-center space-x-2">
                      <div className="text-2xl font-bold text-gray-900">
                        {data.percentage}%
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>

            {detailsOpen && detailsPayload && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={closeDetails}>
                <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-lg font-bold text-gray-900">{detailsPayload.title}</div>
                      <div className="text-sm text-gray-500">{detailsPayload.percentage}% • {detailsPayload.severity}</div>
                    </div>
                    <button onClick={closeDetails} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {detailsPayload.description || 'No additional description available.'}
                  </div>
                </div>
              </div>
            )}

            {/* Professional Assessment */}
            {result.overallAssessment && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Professional Assessment</h3>
                <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Overall Condition</div>
                    <p className="text-gray-900 mt-1">{result.overallAssessment.condition}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Severity Level</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      result.overallAssessment.severity === 'Mild' ? 'bg-green-100 text-green-800' :
                      result.overallAssessment.severity === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {result.overallAssessment.severity}
                    </span>
                  </div>
                  {result.overallAssessment.skinAge && (
                    <div>
                      <div className="text-sm font-medium text-gray-700">Skin Age Estimate</div>
                      <p className="text-gray-900 mt-1">{result.overallAssessment.skinAge}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Skincare Routine */}
            {result.recommendations?.skincare && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Recommended Routine</h3>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-purple-900 mb-2">Personalized Skincare Plan</div>
                      <p className="text-purple-800 text-sm leading-relaxed whitespace-pre-line">
                        {result.recommendations.skincare}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up Recommendations */}
            {result.recommendations?.followup && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Follow-up Care</h3>
                <div className="bg-orange-50 rounded-2xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-orange-900 mb-2">Professional Follow-up</div>
                      <p className="text-orange-800 text-sm leading-relaxed">
                        {result.recommendations.followup}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            {result.summary && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Summary</h3>
                <div className="bg-blue-50 rounded-2xl p-4">
                  <p className="text-blue-900 text-sm leading-relaxed">
                    {result.summary}
                  </p>
                </div>
              </div>
            )}

            {/* Professional Disclaimer - Separate section */}
            <div className="mt-6">
              <div className="bg-gray-50 border-l-4 border-blue-500 rounded-2xl p-4 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">Medical Disclaimer</h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      This AI analysis is for informational purposes only. Please consult with a qualified dermatologist for professional medical advice.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Analysis Details */}
            {showDetailedView && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Details</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Analysis Date:</span>
                    <p>{new Date(analysisState.result.timestamp).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="font-medium">Image Source:</span>
                    <p>{analysisState.result.imageInfo.source === 'camera' ? 'Camera Capture' : 'File Upload'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Processing Time:</span>
                    <p>{Math.round(analysisState.result.processingTime / 1000)}s</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons - Single PDF Generation */}
            <div className="space-y-3 pt-6 pb-24">
              {!analysisState.result?.pdfBlob ? (
                <button
                  onClick={generatePDF}
                  disabled={pdfState.isGenerating}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center hover:from-purple-700 hover:to-indigo-700"
                >
                  {pdfState.isGenerating ? (
                    <>
                      <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Creating Report...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">📄</span>
                      Generate PDF Report
                    </>
                  )}
                </button>
              ) : (
                <a
                  href={downloadUrl}
                  download={`Skin_Analysis_Report_${appState.patientData?.name || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center hover:from-green-700 hover:to-emerald-700 no-underline"
                >
                  <span className="mr-2">📥</span>
                  Download PDF Report
                </a>
              )}

              {/* Contact Button - WhatsApp or Custom Booking */}
              {doctorWhatsAppUrl ? (
                doctorWhatsAppUrl.includes('wa.me') || doctorWhatsAppUrl.includes('whatsapp') ? (
                  <a
                    href={doctorWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center hover:from-green-600 hover:to-green-700 no-underline shadow-lg hover:shadow-xl"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 4.315 1.919 6.066l-1.285 4.685 4.849-1.282z" />
                    </svg>
                    Contact Doctor on WhatsApp
                  </a>
                ) : (
                  <a
                    href={doctorWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center hover:from-blue-600 hover:to-purple-700 no-underline shadow-lg hover:shadow-xl"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Book Appointment Online
                  </a>
                )
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowDetailedView(!showDetailedView)}
                    className="bg-gray-100 text-gray-700 font-medium py-2 px-3 rounded-lg text-sm flex-1 hover:bg-gray-200 transition-colors"
                  >
                    {showDetailedView ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hidden PDF Preview for generation */}
        <div style={{
          position: 'fixed',
          left: '0',
          top: '0',
          width: '816px',
          height: 'auto',
          zIndex: -1000,
          opacity: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <PDFPreview
            ref={pdfPreviewRef}
            analysisData={analysisState.result}
            patientData={appState.patientData}
            capturedImage={appState.capturedImage}
            doctorInfo={doctorInfo}
          />
        </div>

        {/* PDF Generation Overlay - Minimalistic */}
        {(pdfState.isGenerating || pdfState.isGenerated) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 text-center">

              {pdfState.isGenerating ? (
                // Generating State
                <>
                  <div className="w-12 h-12 mx-auto mb-4 border-2 border-gray-200 border-t-black rounded-full animate-spin"></div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Creating Report
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Generating your analysis PDF...
                  </p>
                </>
              ) : (
                // Success State
                <>
                  <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>

                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Report Ready!
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Your PDF report has been generated successfully.
                  </p>

                  <div className="space-y-3">
                    <a
                      href={downloadUrl}
                      download={`Skin_Analysis_Report_${appState.patientData?.name || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`}
                      className="inline-block w-full bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors text-center"
                    >
                      📥 Download Report
                    </a>
                    <button
                      onClick={() => setPdfState(prev => ({ ...prev, isGenerated: false }))}
                      className="w-full bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default state (shouldn't reach here)
  return null;
};

export default DocsAnalysis;
